import {
  Archive,
  BookOpen02,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Package,
  Target04,
  User01,
  X,
  XClose,
} from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { useApproveSampleRequest, useDenySampleRequest } from "@/api/collaborations";
import {
  type SampleRequestFilters,
  type SampleRequestGroup,
  type SampleRequestItem,
  type SampleRequestTab,
  type SampleRequestView,
  useIgnoreSampleRequest,
  useSampleRequestGroupItems,
} from "@/api/sample-requests";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { RuleSignalIcon } from "@/components/rules/rule-signal-icon";
import { RuleSourceTooltip } from "@/components/rules/rule-source-tooltip";
import { useCurrency } from "@/hooks/use-currency";
import { ProductGroupRow } from "@/pages/operations/sample-requests/product-group-row";
import { toSelectedSample, useSampleSelection } from "@/pages/operations/sample-requests/sample-selection-context";
import { success } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";

const CHILD_PAGE_SIZE = 50;
const UNLIMITED_STOCK_SENTINEL = 999999;

const formatUnits = (units: number | null) => {
  if (units == null) return "—";
  if (units >= UNLIMITED_STOCK_SENTINEL) return "Unlimited";
  return units;
};

const formatMoney = (amount: number | null, currency: string, fractionDigits: number) =>
  amount == null
    ? "—"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(amount);

function TimeLeftCell({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-tertiary">—</span>;
  const hours = Math.round((new Date(iso).getTime() - Date.now()) / 3_600_000);
  if (hours < 0) return <span className="text-sm font-medium text-error-primary">Expired</span>;
  return (
    <span className={cx("text-sm font-medium", hours < 12 ? "text-error-primary" : "text-warning-primary")}>
      {hours}hr
    </span>
  );
}

function CreatorNameButton({
  creatorId,
  tiktokOpenId,
  name,
  avatarUrl,
  onSelect,
  className,
}: {
  creatorId: string | null | undefined;
  tiktokOpenId: string;
  name: string;
  /** Pass to render an avatar (or fallback icon) as part of the clickable target; omit to render name only. */
  avatarUrl?: string | null;
  onSelect: (creator: { creatorId?: string; tiktokOpenId: string }) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSelect({ creatorId: creatorId ?? undefined, tiktokOpenId });
      }}
      className={cx("flex min-w-0 items-center gap-2.5 text-left text-sm font-medium text-primary", className)}
    >
      {avatarUrl !== undefined &&
        (avatarUrl ? (
          <img src={avatarUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" />
        ) : (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
            <User01 className="size-4 text-quaternary" />
          </div>
        ))}
      <span className="truncate">{name}</span>
    </button>
  );
}

const creatorHandle = (creator: NonNullable<SampleRequestItem["creator"]>) =>
  creator.username ? `@${creator.username}` : creator.nickname;

function RowActions({ item }: { item: SampleRequestItem }) {
  const approve = useApproveSampleRequest();
  const deny = useDenySampleRequest();
  const ignore = useIgnoreSampleRequest();
  const busy = approve.isPending || deny.isPending || ignore.isPending;
  const canCollab = !!item.collaborationId;
  const name = (item.creator && creatorHandle(item.creator)) ?? item.sku?.name ?? "creator";

  return (
    <div className="flex flex-row items-center justify-end gap-1">
      <ButtonUtility
        size="xs"
        color="tertiary"
        tooltip="Ignore"
        icon={Archive}
        isDisabled={busy}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          void ignore.mutateAsync(item.applicationId).then(() => success(`Ignored request for ${name}`));
        }}
      />
      <ButtonUtility
        size="xs"
        color="tertiary"
        tooltip="Reject"
        icon={X}
        isDisabled={busy || !canCollab}
        className="text-error-primary"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          if (!item.collaborationId) return;
          void deny
            .mutateAsync({
              collaborationId: item.collaborationId,
              applicationId: item.applicationId,
              reason: "OTHER",
            })
            .then(() => success(`Rejected request for ${name}`));
        }}
      />
      <ButtonUtility
        size="xs"
        color="tertiary"
        tooltip="Approve"
        icon={Check}
        isDisabled={busy || !canCollab}
        className="text-success-primary"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          if (!item.collaborationId) return;
          void approve
            .mutateAsync({
              collaborationId: item.collaborationId,
              applicationId: item.applicationId,
            })
            .then(() => success(`Approved request for ${name}`));
        }}
      />
    </div>
  );
}

function CollabPlanIcon({ isTarget }: { isTarget: boolean }) {
  const Icon = isTarget ? Target04 : BookOpen02;
  return (
    <Tooltip title={isTarget ? "Target collaboration" : "Open collaboration"}>
      <TooltipTrigger className="inline-flex">
        <Icon className="size-4 text-quaternary" />
      </TooltipTrigger>
    </Tooltip>
  );
}

function BrandSafetyCell({ value }: { value: SampleRequestItem["brandSafety"] }) {
  if (!value) return <span className="text-tertiary">—</span>;
  return (
    <span className={cx("text-sm font-medium", value === "high" ? "text-error-primary" : "text-tertiary")}>
      {value === "high" ? "High Risk" : "Low Risk"}
    </span>
  );
}

function SampleDecisionStatus({ item, tab }: { item: SampleRequestItem; tab: SampleRequestTab }) {
  const approved = tab === "approved";
  const isRule = item.decision?.source === "rule";
  const ManualIcon = approved ? CheckCircle : XClose;

  return (
    <RuleSourceTooltip appliedBy={item.decision} action={approved ? "approved" : "rejected"}>
      <span
        className={cx(
          "inline-flex items-center gap-1.5 text-sm font-medium",
          approved ? "text-success-primary" : "text-error-primary",
        )}
      >
        {approved ? "Approved" : "Rejected"}
        {isRule ? <RuleSignalIcon /> : <ManualIcon className="size-3.5 shrink-0" />}
      </span>
    </RuleSourceTooltip>
  );
}

const HeaderStat = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex min-w-[72px] flex-col">
    <span className="text-sm font-medium text-primary">{value}</span>
    <span className="text-xs text-tertiary">{label}</span>
  </div>
);

const Th = ({ children, className }: { children?: ReactNode; className?: string }) => (
  <th className={cx("px-3 py-2 text-left text-xs font-semibold text-quaternary", className)}>{children}</th>
);

const Td = ({ children, className }: { children?: ReactNode; className?: string }) => (
  <td className={cx("px-3 py-3 text-sm text-tertiary", className)}>{children}</td>
);

function SkuChildTable({
  items,
  merchantCurrency,
  onSelectCreator,
  tab,
}: {
  items: SampleRequestItem[];
  tab: SampleRequestTab;
  merchantCurrency: string;
  onSelectCreator: (creator: { creatorId?: string; tiktokOpenId: string }) => void;
}) {
  const { isSelected, toggle, toggleMany } = useSampleSelection();
  const showStatus = tab === "approved" || tab === "rejected";

  const allSelected = items.length > 0 && items.every((i) => isSelected(i.applicationId));
  const someSelected = items.some((i) => isSelected(i.applicationId));

  const toggleAll = (checked: boolean) => toggleMany(items.map(toSelectedSample), checked);

  return (
    <table className="w-full table-fixed">
      <thead className="bg-secondary">
        <tr>
          <Th className="w-10 pl-5">
            <Checkbox
              isSelected={allSelected}
              isIndeterminate={someSelected && !allSelected}
              onChange={toggleAll}
              aria-label="Select all"
            />
          </Th>
          <Th>Creators</Th>
          <Th className="w-20">Time Left</Th>
          <Th className="w-20">Units</Th>
          <Th className="w-24">Post Rate</Th>
          <Th className="w-28">GMVL30D</Th>
          <Th className="w-24">GPM</Th>
          <Th className="w-28">Videos/Sample</Th>
          <Th className="w-28">
            <span className="inline-flex items-center gap-1">
              Collab Plan
              <Tooltip title="Collab plan" description="Open collab (book icon) vs. target collab (target icon).">
                <TooltipTrigger className="inline-flex">
                  <HelpCircle className="size-3.5 text-quaternary" />
                </TooltipTrigger>
              </Tooltip>
            </span>
          </Th>
          <Th className="w-28">Brand Safety</Th>
          {showStatus && <Th className="w-28">Status</Th>}
          <Th className="w-24 pr-5 text-right">{""}</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.applicationId} className="border-t border-secondary">
            <Td className="pl-5">
              <Checkbox
                isSelected={isSelected(item.applicationId)}
                onChange={() => toggle(toSelectedSample(item))}
                aria-label="Select row"
              />
            </Td>
            <Td className="min-w-0">
              {item.creator ? (
                <CreatorNameButton
                  creatorId={item.creator.creatorId}
                  tiktokOpenId={item.creator.openId}
                  name={creatorHandle(item.creator) ?? "Unknown"}
                  avatarUrl={item.creator.avatar}
                  onSelect={onSelectCreator}
                  className="w-full"
                />
              ) : (
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
                    <User01 className="size-4 text-quaternary" />
                  </div>
                  <span className="truncate text-sm font-medium text-primary">Unknown</span>
                </div>
              )}
            </Td>
            <Td>
              <TimeLeftCell iso={item.approveExpirationTime} />
            </Td>
            <Td className="text-primary">{formatUnits(item.units)}</Td>
            <Td>{item.postRate == null ? "—" : `${item.postRate}%`}</Td>
            <Td className="text-primary">{formatMoney(item.gmvAmount, item.gmvCurrency ?? merchantCurrency, 0)}</Td>
            <Td className="text-primary">{formatMoney(item.gpmAmount, item.gpmCurrency ?? merchantCurrency, 2)}</Td>
            <Td className="text-primary">{item.videosSample ?? "—"}</Td>
            <Td>
              <CollabPlanIcon isTarget={!!item.isTargetCollab} />
            </Td>
            <Td>
              <BrandSafetyCell value={item.brandSafety} />
            </Td>
            {showStatus && (
              <Td>
                <SampleDecisionStatus item={item} tab={tab} />
              </Td>
            )}
            <Td className="pr-5">
              <RowActions item={item} />
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CreatorChildTable({ items, tab }: { items: SampleRequestItem[]; tab: SampleRequestTab }) {
  const { isSelected, toggle, toggleMany } = useSampleSelection();
  const showStatus = tab === "approved" || tab === "rejected";

  const allSelected = items.length > 0 && items.every((i) => isSelected(i.applicationId));
  const someSelected = items.some((i) => isSelected(i.applicationId));

  return (
    <table className="w-full table-fixed">
      <thead className="bg-secondary">
        <tr>
          <Th className="w-10 pl-5">
            <Checkbox
              isSelected={allSelected}
              isIndeterminate={someSelected && !allSelected}
              onChange={(checked) => toggleMany(items.map(toSelectedSample), checked)}
              aria-label="Select all"
            />
          </Th>
          <Th>Sample</Th>
          <Th className="w-20">Time Left</Th>
          <Th className="w-20">Units</Th>
          {showStatus && <Th className="w-28">Status</Th>}
          <Th className="w-24 pr-5 text-right">{""}</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.applicationId} className="border-t border-secondary">
            <Td className="pl-5">
              <Checkbox
                isSelected={isSelected(item.applicationId)}
                onChange={() => toggle(toSelectedSample(item))}
                aria-label="Select row"
              />
            </Td>
            <Td className="min-w-0">
              <div className="flex min-w-0 items-center gap-2.5">
                {item.sku?.imageUrl ? (
                  <img src={item.sku.imageUrl} alt="" className="size-8 shrink-0 rounded object-cover" />
                ) : (
                  <div className="flex size-8 shrink-0 items-center justify-center rounded bg-gray-100">
                    <Package className="size-4 text-quaternary" />
                  </div>
                )}
                <span className="truncate text-sm font-medium text-primary">{item.sku?.name ?? "—"}</span>
              </div>
            </Td>
            <Td>
              <TimeLeftCell iso={item.approveExpirationTime} />
            </Td>
            <Td className="text-primary">{formatUnits(item.units)}</Td>
            {showStatus && (
              <Td>
                <SampleDecisionStatus item={item} tab={tab} />
              </Td>
            )}
            <Td className="pr-5">
              <RowActions item={item} />
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function GroupCard({
  group,
  view,
  tab,
  filters,
  onSelectCreator,
}: {
  group: SampleRequestGroup;
  view: SampleRequestView;
  tab: SampleRequestTab;
  filters: SampleRequestFilters;
  onSelectCreator: (creator: { creatorId?: string; tiktokOpenId: string }) => void;
}) {
  const [expanded, setExpanded] = useState(view === "product");
  const merchantCurrency = useCurrency() ?? "GBP";
  const { data, isLoading } = useSampleRequestGroupItems(
    { view, tab, groupId: group.groupId, filters },
    { offset: 0, limit: CHILD_PAGE_SIZE },
    expanded,
  );

  return (
    <div className="overflow-hidden rounded-xl border border-secondary bg-primary">
      {/* biome-ignore lint/a11y/useSemanticElements: nests a real <button> (CreatorNameButton) for creator view; button-in-button is invalid HTML */}
      <div
        role="button"
        tabIndex={0}
        // Creator view nests a separately-named creator button (see CreatorNameButton below), so this
        // row needs its own aria-label — otherwise its accessible name would swallow the creator's
        // name too and make the two indistinguishable to assistive tech / role queries.
        aria-label={view === "creator" ? "Toggle creator group" : undefined}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setExpanded((v) => !v);
        }}
        className="flex w-full items-center gap-4 px-5 py-3 text-left hover:bg-secondary cursor-pointer"
      >
        {expanded ? <ChevronUp className="size-5 text-tertiary" /> : <ChevronDown className="size-5 text-tertiary" />}
        {group.imageUrl ? (
          <img src={group.imageUrl} alt="" className="size-11 shrink-0 rounded-lg object-cover" />
        ) : (
          <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-gray-100">
            <Package className="size-5 text-quaternary" />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          {view === "creator" ? (
            <CreatorNameButton
              creatorId={group.creatorId}
              tiktokOpenId={group.groupId}
              name={group.title}
              onSelect={onSelectCreator}
              className="font-semibold"
            />
          ) : (
            <span className="truncate text-sm font-semibold text-primary">{group.title}</span>
          )}
          {group.subtitle && <span className="truncate text-xs text-tertiary">{group.subtitle}</span>}
        </div>

        {view === "product" ? (
          <ProductGroupRow group={group} />
        ) : view === "sku" ? (
          <div className="flex items-center gap-8">
            <HeaderStat label="Available" value={group.available ?? "—"} />
            <HeaderStat label="Total Allocations" value={group.totalAllocations ?? "—"} />
          </div>
        ) : (
          <div className="flex items-center gap-8">
            <HeaderStat label="Videos per Sample" value={group.videosPerSample ?? "—"} />
            <HeaderStat label="Sample Requests" value={group.requestCount} />
            <HeaderStat label="GPM" value={formatMoney(group.gpmAmount, group.gpmCurrency ?? merchantCurrency, 2)} />
            <HeaderStat
              label="GMV L30D"
              value={formatMoney(group.gmvAmount, group.gmvCurrency ?? merchantCurrency, 0)}
            />
            <HeaderStat label="Post Rate" value={group.postRate == null ? "—" : `${group.postRate}%`} />
            <HeaderStat label="Safety Status" value={group.brandSafety === "high" ? "High Risk" : "Low Risk"} />
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t border-secondary">
          {isLoading ? (
            <p className="px-5 py-6 text-sm text-tertiary">Loading…</p>
          ) : !data || data.items.length === 0 ? (
            <p className="px-5 py-6 text-sm text-tertiary">No requests.</p>
          ) : view === "creator" ? (
            <CreatorChildTable items={data.items} tab={tab} />
          ) : (
            <SkuChildTable
              items={data.items}
              merchantCurrency={merchantCurrency}
              onSelectCreator={onSelectCreator}
              tab={tab}
            />
          )}
        </div>
      )}
    </div>
  );
}
